import { useCallback } from 'react';
import logger from '../utils/logger';

// Create a singleton state manager
class RoomStateManager {
  constructor() {
    this.states = {
      reply: new Map(),
      edit: new Map(),
    };
  }

  getState(type, roomId) {
    if (!roomId) return null;
    const result = this.states[type].get(roomId) || null;
    logger.debug(`[RoomStateManager] Getting ${type} state for room ${roomId}:`, result);
    return result;
  }

  setState(type, roomId, state) {
    if (!roomId) return;
    logger.debug(`[RoomStateManager] Setting ${type} state for room ${roomId}:`, state);
    if (state === null || state === undefined) {
      logger.debug(`[RoomStateManager] Deleting state for room ${roomId}`);
      this.states[type].delete(roomId);
    } else {
      this.states[type].set(roomId, state);
    }
    logger.debug(`[RoomStateManager] Current ${type} states:`, Array.from(this.states[type].entries()));
  }

  clearState(type, roomId) {
    if (!roomId) return;
    logger.debug(`[RoomStateManager] Clearing ${type} state for room ${roomId}`);
    this.states[type].delete(roomId);
  }

  // Debug method to see all states
  getAllStates() {
    return {
      reply: Array.from(this.states.reply.entries()),
      edit: Array.from(this.states.edit.entries()),
    };
  }
}

// Singleton instance
const stateManager = new RoomStateManager();

/**
 * Hook for managing per-room state that persists across navigation
 * @param {string} type - Type of state ('reply' or 'edit')
 * @param {string} roomId - Room identifier
 * @returns {object} State management functions
 */
export const usePerRoomState = (type, roomId) => {
  const getState = useCallback(() => {
    const result = stateManager.getState(type, roomId);
    logger.debug(`[usePerRoomState] Getting ${type} state for room ${roomId}:`, result);
    return result;
  }, [type, roomId]);

  const setState = useCallback((state) => {
    logger.debug(`[usePerRoomState] Setting ${type} state for room ${roomId}:`, state);
    stateManager.setState(type, roomId, state);
    logger.debug('[usePerRoomState] All states after set:', stateManager.getAllStates());
  }, [type, roomId]);

  const clearState = useCallback(() => {
    logger.debug(`[usePerRoomState] Clearing ${type} state for room ${roomId}`);
    stateManager.clearState(type, roomId);
  }, [type, roomId]);

  return {
    getState,
    setState,
    clearState,
  };
};

// Export the singleton state manager for direct access
export { stateManager };

export default usePerRoomState;